import { useState, useEffect, useCallback } from 'react';
import { useOutletContext, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Table, TableBody, TableCell, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { FileText, Scale, TrendingUp, TrendingDown, Minus, Lock, LockOpen, ShieldAlert, Archive } from 'lucide-react';
import { IvaReconciliation } from '../components/IvaReconciliation';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const fmtMoney = (n) => '$ ' + Number(n || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const pad = (n) => String(n).padStart(2, '0');
const rangeFromMonth = (ym) => {
  // ym: "YYYY-MM"
  const [y, m] = ym.split('-').map(Number);
  const last = new Date(y, m, 0).getDate();
  return { date_from: `${y}-${pad(m)}-01`, date_to: `${y}-${pad(m)}-${pad(last)}` };
};
const monthDefault = () => {
  const d = new Date();
  return rangeFromMonth(`${d.getFullYear()}-${pad(d.getMonth() + 1)}`);
};

export default function AccountingIvaPosition() {
  const { user } = useOutletContext();
  const isAdmin = user?.role === 'admin';
  const [range, setRange] = useState(monthDefault());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [closures, setClosures] = useState([]);
  const [closureLoading, setClosureLoading] = useState(false);
  const [closeScope, setCloseScope] = useState(null);  // scope pendiente de cerrar
  const [closeNotes, setCloseNotes] = useState('');
  const [reopenTarget, setReopenTarget] = useState(null);
  const [reopenReason, setReopenReason] = useState('');
  const [saving, setSaving] = useState(false);

  const period = (() => {
    const [y, m] = String(range.date_from || '').split('-');
    return { year: Number(y) || null, month: Number(m) || null };
  })();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (range.date_from) params.date_from = range.date_from;
      if (range.date_to) params.date_to = range.date_to;
      const pos = await axios.get(`${BACKEND_URL}/api/accounting/iva-position`, { params, withCredentials: true });
      setData(pos.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al cargar Posición de IVA'); } finally { setLoading(false); }
  }, [range]);

  const loadClosures = useCallback(async () => {
    if (!period.year || !period.month) return;
    setClosureLoading(true);
    try {
      const r = await axios.get(`${BACKEND_URL}/api/accounting/closures`, { withCredentials: true });
      const filtered = (r.data.items || []).filter(c => c.period_year === period.year && c.period_month === period.month);
      setClosures(filtered);
    } catch (e) { /* silencioso */ } finally { setClosureLoading(false); }
  }, [period.year, period.month]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadClosures(); }, [loadClosures]);

  const closureByScope = (scope) => closures.find(c => c.scope === scope && c.status === 'closed');

  const closePeriod = async () => {
    if (!isAdmin || !closeScope) return;
    setSaving(true);
    try {
      await axios.post(`${BACKEND_URL}/api/accounting/closures`, {
        period_year: period.year, period_month: period.month, scope: closeScope,
        notes: closeNotes || null,
      }, { withCredentials: true });
      toast.success(`Período ${String(period.month).padStart(2, '0')}/${period.year} cerrado`);
      setCloseScope(null); setCloseNotes('');
      loadClosures();
    } catch (e) { toast.error(e.response?.data?.detail || 'No se pudo cerrar'); } finally { setSaving(false); }
  };

  const reopenPeriod = async () => {
    if (!isAdmin || !reopenTarget) return;
    if (!reopenReason || reopenReason.trim().length < 5) { toast.error('Motivo obligatorio (mínimo 5 caracteres)'); return; }
    setSaving(true);
    try {
      await axios.post(`${BACKEND_URL}/api/accounting/closures/${reopenTarget.closure_id}/reopen`,
        { reason: reopenReason.trim() }, { withCredentials: true });
      toast.success('Período reabierto');
      setReopenTarget(null); setReopenReason('');
      loadClosures();
    } catch (e) { toast.error(e.response?.data?.detail || 'No se pudo reabrir'); } finally { setSaving(false); }
  };

  const buildF2002PdfUrl = () => {
    const url = new URL(`${BACKEND_URL}/api/accounting/iva-f2002.pdf`);
    if (range.date_from) url.searchParams.set('date_from', range.date_from);
    if (range.date_to) url.searchParams.set('date_to', range.date_to);
    return url.toString();
  };

  const buildCitiUrl = () => {
    const url = new URL(`${BACKEND_URL}/api/accounting/iva-citi.zip`);
    if (range.date_from) url.searchParams.set('date_from', range.date_from);
    if (range.date_to) url.searchParams.set('date_to', range.date_to);
    return url.toString();
  };

  const buildUrl = () => {
    const url = new URL(`${BACKEND_URL}/api/accounting/iva-position.pdf`);
    if (range.date_from) url.searchParams.set('date_from', range.date_from);
    if (range.date_to) url.searchParams.set('date_to', range.date_to);
    return url.toString();
  };

  const posMeta = {
    a_pagar: { label: 'SALDO A PAGAR', color: 'text-red-600 border-red-600', Icon: TrendingUp },
    a_favor: { label: 'SALDO A FAVOR', color: 'text-green-600 border-green-600', Icon: TrendingDown },
    neutro: { label: 'SIN SALDO', color: 'text-slate-500 border-slate-400', Icon: Minus },
  };

  return (
    <div className="space-y-6" data-testid="iva-position-page">
      <div className="flex items-center gap-3">
        <Scale className="w-7 h-7 text-indigo-600" />
        <div>
          <h1 className="text-2xl font-bold">Posición de IVA del período</h1>
          <p className="text-sm text-muted-foreground">Débito Fiscal (Ventas) − Crédito Fiscal (Compras) − Percepciones</p>
        </div>
      </div>

      <Card><CardContent className="pt-6">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <Label className="text-xs">Período (mes calendario)</Label>
            <Input
              type="month"
              value={String(range.date_from || '').slice(0, 7)}
              onChange={e => e.target.value && setRange(rangeFromMonth(e.target.value))}
              data-testid="pos-period-month"
            />
          </div>
          <div className="flex gap-2 ml-auto flex-wrap justify-end">
            <Button variant="outline" onClick={() => window.open(buildUrl(), '_blank')} data-testid="pos-pdf">
              <FileText className="w-4 h-4 mr-2" />Posición PDF
            </Button>
            <Button variant="outline" onClick={() => window.open(buildF2002PdfUrl(), '_blank')} data-testid="pos-f2002-pdf" className="border-emerald-400 text-emerald-700 hover:bg-emerald-50">
              <FileText className="w-4 h-4 mr-2" />F2002 Informe PDF
            </Button>
            <Button variant="outline" onClick={() => window.open(buildCitiUrl(), '_blank')} data-testid="pos-citi-zip" className="border-indigo-300 text-indigo-700 hover:bg-indigo-50">
              <Archive className="w-4 h-4 mr-2" />F2002 / CITI ZIP
            </Button>
          </div>
        </div>
      </CardContent></Card>

      {loading ? <p className="text-center py-8 text-muted-foreground">Cargando…</p> : data && (
        <>
          <div className="grid gap-6 md:grid-cols-2">
            <Card data-testid="pos-sales-card"><CardContent className="pt-6">
              <h2 className="text-lg font-semibold mb-1">Débito Fiscal · Ventas</h2>
              <p className="text-xs text-muted-foreground mb-3">{data.sales.count} comprobante(s)</p>
              <Table><TableBody>
                <TableRow><TableCell>Neto Gravado</TableCell><TableCell className="text-right">{fmtMoney(data.sales.neto_gravado)}</TableCell></TableRow>
                <TableRow><TableCell>Exento / No Gravado</TableCell><TableCell className="text-right">{fmtMoney(data.sales.exento)}</TableCell></TableRow>
                <TableRow className="font-bold"><TableCell>IVA Débito Fiscal</TableCell><TableCell className="text-right" data-testid="pos-debito">{fmtMoney(data.sales.iva)}</TableCell></TableRow>
              </TableBody></Table>
            </CardContent></Card>

            <Card data-testid="pos-purchases-card"><CardContent className="pt-6">
              <h2 className="text-lg font-semibold mb-1">Crédito Fiscal · Compras</h2>
              <p className="text-xs text-muted-foreground mb-3">{data.purchases.count} comprobante(s)</p>
              <Table><TableBody>
                <TableRow><TableCell>Neto Gravado</TableCell><TableCell className="text-right">{fmtMoney(data.purchases.neto_gravado)}</TableCell></TableRow>
                <TableRow><TableCell>Exento / No Gravado</TableCell><TableCell className="text-right">{fmtMoney(data.purchases.exento)}</TableCell></TableRow>
                <TableRow className="font-bold"><TableCell>IVA Crédito Fiscal</TableCell><TableCell className="text-right" data-testid="pos-credito">{fmtMoney(data.purchases.iva)}</TableCell></TableRow>
                <TableRow><TableCell>Percepciones / Retenciones sufridas</TableCell><TableCell className="text-right">{fmtMoney(data.purchases.percepciones)}</TableCell></TableRow>
              </TableBody></Table>
            </CardContent></Card>
          </div>

          <Card><CardContent className="pt-6">
            <h2 className="text-lg font-semibold mb-3">Liquidación del período</h2>
            <Table><TableBody>
              <TableRow><TableCell>Débito Fiscal (Ventas)</TableCell><TableCell className="text-right">{fmtMoney(data.debito_fiscal)}</TableCell></TableRow>
              <TableRow><TableCell>(−) Crédito Fiscal (Compras)</TableCell><TableCell className="text-right">{fmtMoney(data.credito_fiscal)}</TableCell></TableRow>
              <TableRow className="font-semibold border-t"><TableCell>Subtotal técnico (DF − CF)</TableCell><TableCell className="text-right" data-testid="pos-saldo-tecnico">{fmtMoney(data.saldo_tecnico)}</TableCell></TableRow>
              <TableRow><TableCell>(−) Percepciones / Retenciones</TableCell><TableCell className="text-right">{fmtMoney(data.percepciones)}</TableCell></TableRow>
            </TableBody></Table>

            {(() => {
              const m = posMeta[data.posicion] || posMeta.neutro;
              const { Icon } = m;
              return (
                <div className={`mt-4 flex items-center justify-center gap-3 rounded-lg border-2 py-6 ${m.color}`} data-testid="pos-result">
                  <Icon className="w-7 h-7" />
                  <div className="text-center">
                    <div className="text-sm font-bold">{m.label}</div>
                    <div className="text-3xl font-bold" data-testid="pos-resultado">{fmtMoney(Math.abs(data.resultado))}</div>
                  </div>
                </div>
              );
            })()}
          </CardContent></Card>

          <IvaReconciliation range={range} />

          {/* v3.24.1 — Cierres del período (IVA Compras + IVA Ventas) */}
          <Card data-testid="pos-closures-card">
            <CardContent className="pt-6 space-y-4">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <h2 className="text-lg font-semibold flex items-center gap-2">
                    <Lock className="w-5 h-5 text-rose-700" />
                    Cierres del período {period.month ? `${String(period.month).padStart(2, '0')}/${period.year}` : ''}
                  </h2>
                  <p className="text-xs text-muted-foreground">Bloquean la carga retroactiva de comprobantes en el Libro IVA correspondiente. Sólo administradores.</p>
                </div>
                <Link to="/accounting/closures">
                  <Button variant="ghost" size="sm" data-testid="btn-closures-full">Ver todos los cierres →</Button>
                </Link>
              </div>

              <div className="grid gap-3 md:grid-cols-2">
                {[
                  { scope: 'iva_purchase', title: 'Libro IVA Compras', boxCls: 'bg-amber-50/40', badgeCls: 'bg-amber-100 text-amber-900 border-amber-300', btnCls: 'bg-amber-700 hover:bg-amber-800' },
                  { scope: 'iva_sales', title: 'Libro IVA Ventas', boxCls: 'bg-sky-50/40', badgeCls: 'bg-sky-100 text-sky-900 border-sky-300', btnCls: 'bg-sky-700 hover:bg-sky-800' },
                ].map(({ scope, title, boxCls, badgeCls, btnCls }) => {
                  const c = closureByScope(scope);
                  return (
                    <div key={scope} className={`rounded-lg border p-3 space-y-2 ${boxCls}`} data-testid={`closure-card-${scope}`}>
                      <div className="flex items-center justify-between">
                        <div className="font-semibold text-sm">{title}</div>
                        {c
                          ? <Badge className={badgeCls}><Lock className="w-3 h-3 mr-1" />Cerrado</Badge>
                          : <Badge variant="outline"><LockOpen className="w-3 h-3 mr-1" />Abierto</Badge>}
                      </div>
                      {c ? (
                        <div className="text-xs text-muted-foreground space-y-0.5">
                          <div>Cerrado el {new Date(c.closed_at).toLocaleString('es-AR', { dateStyle: 'short', timeStyle: 'short' })}</div>
                          <div>por {c.closed_by_name}</div>
                          {c.notes && <div className="italic mt-1">{c.notes}</div>}
                        </div>
                      ) : (
                        <p className="text-xs text-muted-foreground">Se pueden cargar/emitir comprobantes con fecha en este período.</p>
                      )}
                      {isAdmin && (
                        <div className="pt-1">
                          {c
                            ? <Button size="sm" variant="outline" onClick={() => setReopenTarget(c)} data-testid={`reopen-${scope}`}><LockOpen className="w-3.5 h-3.5 mr-1" />Reabrir</Button>
                            : <Button size="sm" onClick={() => { setCloseScope(scope); setCloseNotes(''); }} data-testid={`close-${scope}`} className={btnCls}><Lock className="w-3.5 h-3.5 mr-1" />Cerrar mes</Button>}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {closureLoading && <p className="text-xs text-muted-foreground text-center">Cargando estado…</p>}
            </CardContent>
          </Card>

          {/* Diálogo: Cerrar período (uno u otro scope) */}
          <Dialog open={!!closeScope} onOpenChange={(o) => { if (!o) { setCloseScope(null); setCloseNotes(''); } }}>
            <DialogContent data-testid="close-dialog">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2"><Lock className="w-5 h-5 text-rose-700" />Cerrar {closeScope === 'iva_sales' ? 'Libro IVA Ventas' : 'Libro IVA Compras'}</DialogTitle>
                <DialogDescription>
                  Vas a cerrar <strong>{period.month ? `${String(period.month).padStart(2, '0')}/${period.year}` : ''}</strong> para {closeScope === 'iva_sales' ? 'ventas' : 'compras'}.
                  Después del cierre, nadie podrá {closeScope === 'iva_sales' ? 'emitir' : 'cargar'} comprobantes con fecha de registración en este mes.
                </DialogDescription>
              </DialogHeader>
              <div>
                <Label className="text-xs">Notas (opcional)</Label>
                <Textarea value={closeNotes} onChange={e => setCloseNotes(e.target.value)}
                  placeholder="Ej: DDJJ presentada a ARCA el 05/09" data-testid="close-notes" />
              </div>
              <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900 flex gap-2">
                <ShieldAlert className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <div>Podés reabrirlo después con motivo obligatorio (queda auditado).</div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setCloseScope(null)}>Cancelar</Button>
                <Button onClick={closePeriod} disabled={saving} className="bg-rose-700 hover:bg-rose-800" data-testid="close-confirm">
                  {saving ? 'Cerrando…' : 'Cerrar período'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Diálogo: Reabrir período */}
          <Dialog open={!!reopenTarget} onOpenChange={(o) => { if (!o) { setReopenTarget(null); setReopenReason(''); } }}>
            <DialogContent data-testid="reopen-dialog">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2"><LockOpen className="w-5 h-5 text-emerald-700" />Reabrir período</DialogTitle>
                <DialogDescription>
                  {reopenTarget && (<>Vas a reabrir <strong>{String(reopenTarget.period_month).padStart(2, '0')}/{reopenTarget.period_year}</strong> — {reopenTarget.scope === 'iva_sales' ? 'Libro IVA Ventas' : reopenTarget.scope === 'iva_purchase' ? 'Libro IVA Compras' : 'Contable general'}. El motivo queda auditado.</>)}
                </DialogDescription>
              </DialogHeader>
              <div>
                <Label className="text-xs">Motivo (obligatorio)</Label>
                <Textarea value={reopenReason} onChange={e => setReopenReason(e.target.value)}
                  className="min-h-[100px]" placeholder="Ej: agregar la FC 0001-000123 que faltó al Estudio Contable" data-testid="reopen-reason" />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => { setReopenTarget(null); setReopenReason(''); }}>Cancelar</Button>
                <Button onClick={reopenPeriod} disabled={saving} className="bg-emerald-700 hover:bg-emerald-800" data-testid="reopen-confirm">
                  {saving ? 'Reabriendo…' : 'Reabrir período'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </>
      )}
    </div>
  );
}
